<template>
  <div class="pin-input" :class="{ 'is-loading': loading, 'has-error': !!error }">
    <div class="pin-input__row" role="group" aria-label="PIN code">
      <input
        v-for="(digit, idx) in digits"
        :key="idx"
        :ref="'digit-' + idx"
        class="pin-input__box"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="1"
        autocomplete="one-time-code"
        :disabled="loading"
        :value="digit"
        :aria-label="'Digit ' + (idx + 1) + ' of ' + length"
        @input="onInput($event, idx)"
        @keydown="onKeyDown($event, idx)"
        @focus="onFocus($event, idx)"
        @paste="onPaste($event, idx)"
      />
    </div>
    <p v-if="error" class="pin-input__error" role="alert">{{ error }}</p>
  </div>
</template>

<script>
/**
 * PinInput — reusable N-digit numeric PIN input (default 4).
 *
 * Props:
 *  - value: String     — controlled value (digits only; extras trimmed).
 *  - loading: Boolean  — disables all boxes while a verification is in flight.
 *  - error: String     — inline error text shown below the boxes.
 *  - length: Number    — box count (default 4). The signup backend issues
 *    4-digit codes today, so the default matches reality.
 *  - autofocus: Boolean — focus the first box on mount (default true).
 *
 * Emits:
 *  - input(String)    — fires on every keystroke with the current joined value.
 *  - complete(String) — fires once when all boxes are filled.
 *
 * Behavior:
 *  - Auto-advance between boxes on digit entry.
 *  - Backspace on an empty box jumps back and clears the prior box.
 *  - Paste spreads digits across boxes (trimmed to `length`).
 *  - Non-numeric keystrokes are blocked.
 */
export default {
  name: 'PinInput',
  props: {
    value: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    length: { type: Number, default: 4 },
    autofocus: { type: Boolean, default: true },
  },
  computed: {
    digits() {
      const sanitized = (this.value || '').replace(/\D/g, '').slice(0, this.length)
      const arr = []
      for (let i = 0; i < this.length; i += 1) {
        arr.push(sanitized[i] || '')
      }
      return arr
    },
  },
  watch: {
    loading(val) {
      // When loading resolves without success, re-focus the first empty box
      // so the user can retry without hunting for focus.
      if (!val) {
        this.$nextTick(() => this.focusFirstEmpty())
      }
    },
  },
  mounted() {
    if (this.autofocus) {
      this.$nextTick(() => this.focusBox(0))
    }
  },
  methods: {
    focusBox(idx) {
      const ref = this.$refs['digit-' + idx]
      if (!ref) return
      const el = Array.isArray(ref) ? ref[0] : ref
      if (el && typeof el.focus === 'function') {
        el.focus()
        try { el.select() } catch (e) { /* ignore */ }
      }
    },
    focusFirstEmpty() {
      const firstEmpty = this.digits.findIndex((d) => !d)
      this.focusBox(firstEmpty === -1 ? this.length - 1 : firstEmpty)
    },
    emitValue(next) {
      this.$emit('input', next)
      if (next.length === this.length) {
        this.$emit('complete', next)
      }
    },
    onInput(e, idx) {
      const raw = String(e.target.value || '').replace(/\D/g, '')
      // Multi-char input (e.g. autofill) — treat as paste-style fill.
      if (raw.length > 1) {
        const spread = raw.slice(0, this.length - idx)
        const next = this.digits.slice()
        for (let i = 0; i < spread.length; i += 1) {
          next[idx + i] = spread[i]
        }
        const joined = next.join('')
        this.emitValue(joined)
        this.$nextTick(() => {
          const target = Math.min(idx + spread.length, this.length - 1)
          this.focusBox(target)
        })
        return
      }
      const ch = raw.slice(-1)
      const next = this.digits.slice()
      next[idx] = ch
      const joined = next.join('')
      this.emitValue(joined)
      if (ch && idx < this.length - 1) {
        this.$nextTick(() => this.focusBox(idx + 1))
      }
    },
    onKeyDown(e, idx) {
      const key = e.key
      // Backspace on empty → jump back and clear prior.
      if (key === 'Backspace' && !this.digits[idx] && idx > 0) {
        e.preventDefault()
        const next = this.digits.slice()
        next[idx - 1] = ''
        this.emitValue(next.join(''))
        this.$nextTick(() => this.focusBox(idx - 1))
        return
      }
      if (key === 'ArrowLeft' && idx > 0) {
        e.preventDefault()
        this.focusBox(idx - 1)
        return
      }
      if (key === 'ArrowRight' && idx < this.length - 1) {
        e.preventDefault()
        this.focusBox(idx + 1)
        return
      }
      // Block non-numeric characters. Allow modifier combos (copy/paste/select-all).
      if (key && key.length === 1 && !/[0-9]/.test(key) && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
      }
    },
    onFocus(e, idx) {
      try { e.target.select() } catch (err) { /* ignore */ }
    },
    onPaste(e, idx) {
      e.preventDefault()
      const clip = (e.clipboardData || window.clipboardData)
      if (!clip) return
      const pasted = (clip.getData('text') || '').replace(/\D/g, '')
      if (!pasted) return
      const spread = pasted.slice(0, this.length - idx)
      const next = this.digits.slice()
      for (let i = 0; i < spread.length; i += 1) {
        next[idx + i] = spread[i]
      }
      const joined = next.join('')
      this.emitValue(joined)
      this.$nextTick(() => {
        const target = Math.min(idx + spread.length, this.length - 1)
        this.focusBox(target)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

.pin-input {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.pin-input__row {
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: nowrap;

  @media (max-width: 480px) {
    gap: 10px;
  }
}

.pin-input__box {
  width: 48px;
  height: 56px;
  box-sizing: border-box;
  padding: 0;
  font-family: 'SF Mono', ui-monospace, 'Menlo', 'Consolas', monospace;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  color: var(--signup-text);
  background: var(--signup-surface);
  border: 1px solid var(--signup-border-soft);
  border-radius: 12px;
  caret-color: var(--signup-brand);
  transition: border-color 140ms ease, box-shadow 140ms ease, transform 120ms ease, background 140ms ease;

  &:hover:not(:focus):not(:disabled) {
    border-color: #d5cde8;
  }

  &:focus {
    outline: none;
    border-color: var(--signup-brand);
    box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.18);
    background: var(--signup-surface);
  }

  &:disabled {
    background: #f4f1fa;
    color: var(--signup-text-muted);
    cursor: not-allowed;
  }

  // Filled state — subtle emphasis so the user sees progress.
  &:not(:placeholder-shown),
  &[value]:not([value=""]) {
    border-color: #d5cde8;
    background: #fbfaff;
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 52px;
    font-size: 22px;
  }
}

.pin-input.has-error .pin-input__box {
  border-color: rgba(204, 48, 48, 0.5);
  background: #fff8f8;

  &:focus {
    border-color: var(--signup-danger);
    box-shadow: 0 0 0 3px rgba(204, 48, 48, 0.15);
  }
}

.pin-input.is-loading .pin-input__box {
  opacity: 0.75;
}

.pin-input__error {
  font-size: 13px;
  font-weight: 600;
  color: var(--signup-danger);
  margin: 4px 0 0;
  text-align: center;
  line-height: 1.4;
}
</style>
